import { TFunction } from "i18next";
import { ManifestSign, TheGameMarket } from "./thegamemanifest";

const MARKET_MATCH_ODDS_FT = 1;
const MARKET_MATCH_ODDS_HT = 2;
const MARKET_GOALS_FT = 3;
const MARKET_GOALS_HT = 4;
const MARKET_PLAYER_SCORES = 5;
const MARKET_CORNERS = 6;
const MARKET_CARDS = 7;

const labelKeyByType: Record<number, string> = {
  [MARKET_MATCH_ODDS_FT]: "match_odds_ft",
  [MARKET_MATCH_ODDS_HT]: "match_odds_ht",
  [MARKET_GOALS_FT]: "goals_ft",
  [MARKET_GOALS_HT]: "goals_ht",
  [MARKET_PLAYER_SCORES]: "player_scores",
  [MARKET_CORNERS]: "corners_ft",
  [MARKET_CARDS]: "cards_ft",
};

const lineMarketTypes = [MARKET_GOALS_FT, MARKET_GOALS_HT, MARKET_CORNERS, MARKET_CARDS];

// The backend only sends the player's name inside the label ("<name> scores (FT)").
const PLAYER_SCORES_LABEL = /^(.+?)\s+scores\s+\(FT\)$/i;

// Names and teams can contain apostrophes, which i18next would otherwise HTML-escape.
const options = { ns: "markets", interpolation: { escapeValue: false } };

function lineOf(market: TheGameMarket): string | undefined {
  return market.line === undefined || market.line === null ? undefined : String(market.line);
}

// Falls back to the backend text whenever the type, line or player isn't recognised.
export function getMarketLabel(market: TheGameMarket, t: TFunction): string {
  const key = market.marketTypeId === undefined ? undefined : labelKeyByType[market.marketTypeId];
  if (!key) {
    return market.label;
  }
  if (market.marketTypeId === MARKET_PLAYER_SCORES) {
    const player = PLAYER_SCORES_LABEL.exec(market.label)?.[1];
    return player ? t(key, { ...options, player, defaultValue: market.label }) : market.label;
  }
  if (lineMarketTypes.includes(market.marketTypeId!)) {
    const line = lineOf(market);
    return line ? t(key, { ...options, line, defaultValue: market.label }) : market.label;
  }
  return t(key, { ...options, defaultValue: market.label });
}

export function getSelectionLabel(market: TheGameMarket, sign: ManifestSign, t: TFunction): string {
  const raw = market.selections[sign] ?? sign;
  const type = market.marketTypeId;

  if (type === MARKET_MATCH_ODDS_FT || type === MARKET_MATCH_ODDS_HT) {
    if (sign === "X") {
      return t("draw", { ...options, defaultValue: raw });
    }
    return t(`teams:${raw}`, { interpolation: options.interpolation, defaultValue: raw });
  }
  if (type === MARKET_PLAYER_SCORES) {
    return sign === "1" ? t("yes", { ...options, defaultValue: raw }) : sign === "X" ? t("no", { ...options, defaultValue: raw }) : raw;
  }
  if (type !== undefined && lineMarketTypes.includes(type)) {
    const line = lineOf(market);
    if (line && sign === "1") {
      return t("over", { ...options, line, defaultValue: raw });
    }
    if (line && sign === "X") {
      return t("under", { ...options, line, defaultValue: raw });
    }
  }
  return raw;
}
